import { FC } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import styled from "styled-components";

import { FormDataT } from "./ScheduleModal";
import FormField from "@/components/Forms/FormField";
import { Label } from "@/components/Forms";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/api";
import { ChannelResource } from "@/types/resources";
import useDidUpdate from "@/hooks/useDidUpdate";

const CheckboxHolder = styled.div`
  margin-top: -8px;
`;

const StyledLabel = styled(Label)`
  position: relative;
  cursor: pointer;
  user-select: none;
  margin: 0;
  padding-left: 25px;
`;

const Checkmark = styled.span`
  width: 16px;
  height: 16px;
  top: 0;
  bottom: 0;
  left: 0;
  margin: auto 0;
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 1px;
  transition: 0.2s ease-out all;
  &::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
  }
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 1px;
  width: 1px;

  &:checked ~ ${Checkmark} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: ${({ theme }) => theme.colors.purple};
    &::after {
      display: block;
    }
  }
`;

interface Props {
  control: Control<FormDataT>;
  index: number;
  setValue: UseFormSetValue<FormDataT>;
}

const PostVariationField: FC<Props> = ({ control, index, setValue }) => {
  const { data: channels } = useQuery(
    ["channels", { onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    }
  );

  const isInstant = useWatch<FormDataT, `variations.${number}.is_instant`>({
    control,
    name: `variations.${index}.is_instant`,
  });

  const channelId = useWatch<FormDataT, `variations.${number}.channel_id`>({
    control,
    name: `variations.${index}.channel_id`,
  });

  useDidUpdate(() => {
    if (isInstant) {
      setValue(`variations.${index}.published_at`, "");
    }
  }, [isInstant]);

  useDidUpdate(() => {
    if (channelId) {
      api
        .get<never, { median: number | null }>(
          "/v1/editorial/channel-site-visits-median",
          {
            params: {
              channel_id: channelId,
            },
          }
        )
        .then(({ median }) => {
          setValue(`variations.${index}.site_visit_benchmark`, median ?? 0);
        });
    }
  }, [channelId]);

  return (
    <>
      <FormField<FormDataT, number>
        name={`variations.${index}.channel_id`}
        control={control}
        label="Facebook Page"
        type="select"
        options={channels || []}
      />
      <FormField<FormDataT, number>
        name={`variations.${index}.repost_channel_id`}
        control={control}
        label="Repost Facebook Page"
        type="select"
        options={channels || []}
      />
      <FormField<FormDataT>
        label="Date & Time"
        control={control}
        name={`variations.${index}.published_at`}
        type="datepicker"
        disabled={isInstant}
      />
      <CheckboxHolder>
        <StyledLabel>
          Instant post
          <Input
            type="checkbox"
            checked={isInstant}
            onChange={() =>
              setValue(`variations.${index}.is_instant`, !isInstant)
            }
          />
          <Checkmark />
        </StyledLabel>
      </CheckboxHolder>
      <FormField<FormDataT>
        label="Site Visit Benchmark"
        control={control}
        placeholder="Add site visit benchmark"
        name={`variations.${index}.site_visit_benchmark`}
      />
      <FormField<FormDataT>
        label="Reach Duration (min)"
        control={control}
        placeholder="Add reach duration"
        disabled
        name={`variations.${index}.reach_duration`}
      />
    </>
  );
};

export default PostVariationField;
